import ZenProperties from "./ZenCore/ZenProperties.js";

/**
 * Wraps a position inside a RolandStructuredObject's data view together with a
 * ZenProperties descriptor. Provides name-based get/set (e.g. `getUint32("LEVEL")`)
 * and breadcrumb-style navigation into nested sub-structures.
 *
 * A descriptor is an entry in the flat ZenProperties map — both "leaf" blocks
 * and composite/group-like structures share the same shape: an object with a
 * `parameters` map. A parameter that has a `.block` reference is itself a
 * sub-structure; one without is a leaf field.
 */
export class PropertyAccessor {
    /**
     * @param {RolandStructuredObject} rootObject - object whose `data`/`start` anchor this accessor
     * @param {number} byteOffset - offset from `rootObject.start` for this descriptor
     * @param {object} descriptor - ZenProperties entry describing this position
     */
    constructor(rootObject, byteOffset, descriptor) {
        this.rootObject = rootObject;
        this.byteOffset = byteOffset;
        this.descriptor = descriptor;
    }

    /**
     * Navigate further down the structure using a breadcrumb path such as
     * "PCMT_PTL[2].PTL_FENV". Returns a new PropertyAccessor at that position.
     * @param {string} path
     * @returns {PropertyAccessor}
     */
    at(path) {
        if (!path) return this;
        let cur = this;
        for (const seg of PropertyAccessor._parsePath(path))
            cur = cur._step(seg);
        return cur;
    }

    _step(seg) {
        const param = this.descriptor.parameters[seg.name];
        if (!param)
            throw new Error(`No parameter "${seg.name}" in ${this.descriptor.name}`);
        if (!param.block)
            throw new Error(`"${seg.name}" is a leaf, cannot navigate into it`);

        const count = param.count ?? 1;
        if (seg.index >= count)
            throw new Error(`Index ${seg.index} out of range for "${seg.name}" (count=${count})`);

        const blockStride = param.blockByteLength ?? 0;
        const subOffset = param.byteOffset + seg.index * blockStride;
        return new PropertyAccessor(this.rootObject, this.byteOffset + subOffset, param.block);
    }

    _leaf(name) {
        const p = this.descriptor.parameters[name];
        if (!p)
            throw new Error(`No parameter "${name}" in ${this.descriptor.name}`);
        return p;
    }

    _absolute(name) {
        return this.rootObject.start + this.byteOffset + this._leaf(name).byteOffset;
    }

    getUint8(name)  { return this.rootObject.data.getUint8(this._absolute(name)); }
    getUint16(name) { return this.rootObject.data.getUint16(this._absolute(name), true); }
    getUint32(name) { return this.rootObject.data.getUint32(this._absolute(name), true); }
    getInt8(name)   { return this.rootObject.data.getInt8(this._absolute(name)); }
    getInt16(name)  { return this.rootObject.data.getInt16(this._absolute(name), true); }
    getInt32(name)  { return this.rootObject.data.getInt32(this._absolute(name), true); }

    setUint8(name, v)  { this.rootObject.data.setUint8(this._absolute(name), v); }
    setUint16(name, v) { this.rootObject.data.setUint16(this._absolute(name), v, true); }
    setUint32(name, v) { this.rootObject.data.setUint32(this._absolute(name), v, true); }
    setInt8(name, v)   { this.rootObject.data.setInt8(this._absolute(name), v); }
    setInt16(name, v)  { this.rootObject.data.setInt16(this._absolute(name), v, true); }
    setInt32(name, v)  { this.rootObject.data.setInt32(this._absolute(name), v, true); }

    /**
     * NAME_1 ... NAME_16 fields have descriptor byteLength 1 by convention but
     * actually span the full 16-byte name. Use this as the string width.
     */
    _stringLength(name, p) {
        return /^NAME_\d+$/.test(name) ? 16 : p.byteLength;
    }

    /**
     * Read the leaf as a string (using the parameter's byteLength, or 16 for NAME_*).
     */
    getString(name) {
        const p = this._leaf(name);
        const len = this._stringLength(name, p);
        const arr = new Uint8Array(this.rootObject.data.buffer, this._absolute(name), len);
        return String.fromCharCode.apply(String, arr);
    }

    /**
     * Write a string to the leaf, padded/truncated to the parameter's byteLength.
     */
    setString(name, value) {
        const p = this._leaf(name);
        const len = this._stringLength(name, p);
        const abs = this._absolute(name);
        const padded = (value ?? "").substring(0, len).padEnd(len, " ");
        for (let i = 0; i < len; i++) {
            const c = padded.codePointAt(i);
            this.rootObject.data.setUint8(abs + i, (c !== undefined && c < 128) ? c : 0x20);
        }
    }

    /**
     * Read a property using the descriptor's byteLength to pick the right
     * accessor. NAME_* fields and any leaf with byteLength > 4 are treated
     * as strings. Returns the raw integer / string.
     */
    get(name) {
        const prop = this._leaf(name);

        // If the byteOffset is a string, it is a property of the root object
        if ( typeof prop.byteOffset === 'string' ) {
            return [this.rootObject[prop.byteOffset], undefined];
        }

        // NAME_1 ... NAME_16 are 16-byte strings even though their descriptor
        // byteLength is 1. Treat them as strings and do not fall through to
        // the numeric accessors.
        if (/^NAME_\d+$/.test(name)) {
            const s = this.getString(name).replace(/\s+$/, "");
            return [s, undefined];
        }

        let val;
        if (prop.byteLength > 4) {
            val = this.getString(name).replace(/\s+$/, "");
        } else {
            switch (prop.byteLength) {
                case 1: val = this.getUint8(name); break;
                case 2: val = this.getUint16(name); break;
                case 4: val = this.getUint32(name); break;
            }
        }
        //throw new Error(`Unsupported byteLength ${prop.byteLength} for "${name}"`);

        if (prop.values) {
            const displayVal = Array.isArray(prop.values)
                ? prop.values[ val - (prop.dataRange ? prop.dataRange[0] : 0) ]
                : prop.values[val];
            if (displayVal===undefined)
                return `Invalid value ${val}`;
            return [val,displayVal];
        }
        
        if (prop.displayModifier) {
            let displayVal;
            if (typeof prop.displayModifier === 'function') {
                displayVal = prop.displayModifier(val,this,blockByteOffset);
            } else {
                if (!displayModifiers[prop.displayModifier])
                    throw `Invalid display modifier ${prop.displayModifier}`;
                else
                    prop.displayModifier = displayModifiers[prop.displayModifier];
                displayVal = prop.displayModifier(val,this,blockByteOffset);
            }
            return [val,displayVal];
        }
        return [val,undefined];


    }

    /**
     * Counterpart of `get` — picks signed/string accessor by descriptor.
     */
    set(name, value) {
        const p = this._leaf(name);
        if (p.byteLength > 4 || /^NAME_\d+$/.test(name))
            return this.setString(name, value);
        switch (p.byteLength) {
            case 1: return this.setInt8(name, value);
            case 2: return this.setInt16(name, value);
            case 4: return this.setInt32(name, value);
        }
        throw new Error(`Unsupported byteLength ${p.byteLength} for "${name}"`);
    }

    /**
     * All parameter names on the current descriptor (leaves and sub-blocks).
     * @returns {string[]}
     */
    keys() {
        return Object.keys(this.descriptor.parameters);
    }

    /**
     * Names of leaf parameters (no sub-structure) on the current descriptor.
     * @returns {string[]}
     */
    leafKeys() {
        return Object.entries(this.descriptor.parameters)
            .filter(([, p]) => !p.block)
            .map(([k]) => k);
    }

    /**
     * Names of composite/sub-block parameters on the current descriptor.
     * @returns {string[]}
     */
    blockKeys() {
        return Object.entries(this.descriptor.parameters)
            .filter(([, p]) => !!p.block)
            .map(([k]) => k);
    }

    /** True if `name` is a parameter on the current descriptor. */
    has(name) { return !!this.descriptor.parameters[name]; }

    /** True if `name` is a sub-structure (composite). */
    isBlock(name) { return !!(this.descriptor.parameters[name]?.block); }

    /** True if `name` is a leaf field. */
    isLeaf(name) {
        const p = this.descriptor.parameters[name];
        return !!p && !p.block;
    }

    /**
     * Repeat count for a sub-structure entry (1 if not an array, undefined if
     * the name isn't a sub-structure).
     */
    count(name) {
        const p = this.descriptor.parameters[name];
        if (!p || !p.block) return undefined;
        return p.count ?? 1;
    }

    /**
     * Iterate over all instances of a sub-structure, calling `cb(child, i)`
     * for each. `child` is a PropertyAccessor positioned at index `i`.
     * @param {string} name
     * @param {(child: PropertyAccessor, index: number) => void} cb
     */
    forEach(name, cb) {
        const c = this.count(name);
        if (c === undefined)
            throw new Error(`"${name}" is not a sub-structure on ${this.descriptor.name}`);
        for (let i = 0; i < c; i++)
            cb(this.child(name, i), i);
    }

    /**
     * Get a single sub-structure child as its own PropertyAccessor.
     * @param {string} name
     * @param {number} [index=0]
     * @returns {PropertyAccessor}
     */
    child(name, index = 0) {
        return this._step({ name, index });
    }

    /**
     * Descriptor entry for a parameter (leaf or sub-block).
     */
    describe(name) {
        return this.descriptor.parameters[name];
    }

    /**
     * Parse a breadcrumb path like "FOO[3].BAR.BAZ[1]" into
     * [{name:"FOO",index:3},{name:"BAR",index:0},{name:"BAZ",index:1}].
     * @param {string} path
     * @returns {Array<{name:string,index:number}>}
     */
    static _parsePath(path) {
        return path.split(".").map(seg => {
            const m = seg.match(/^([A-Za-z_][A-Za-z_0-9]*)(?:\[(\d+)\])?$/);
            if (!m) throw new Error(`Bad path segment "${seg}"`);
            return { name: m[1], index: m[2] !== undefined ? parseInt(m[2], 10) : 0 };
        });
    }

    /**
     * Build an accessor rooted on a RolandStructuredObject. The descriptor is
     * looked up from the object's `propertiesName`; pass a `path` to descend
     * further.
     * @param {RolandStructuredObject} obj
     * @param {string} [path]
     * @returns {PropertyAccessor}
     */
    static fromObject(obj, path) {
        const descriptor = ZenProperties[obj.propertiesName];
        if (!descriptor)
            throw new Error(`No ZenProperties entry for "${obj.propertiesName}"`);
        const root = new PropertyAccessor(obj, 0, descriptor);
        return path ? root.at(path) : root;
    }
}
